import {
  Blocks,
  Briefcase,
  GraduationCap,
  LifeBuoy,
  Megaphone,
  Newspaper,
  Rocket,
  ShoppingBag,
  Terminal,
  Users,
} from 'lucide-react'
import type { DropdownNavItem } from './dropdown-navigation'

/**
 * The public navbar's entries: Pricing, the Solutions menu (who Brew is
 * for: marketers, startups, agencies, ecommerce), the Resources menu, and
 * Docs. MCP and the sign-up CTA sit on the bar's right. Every link points at
 * a live page — no placeholders; the audiences land on the page that serves
 * them best until each has its own.
 */
export const NAV_ITEMS: ReadonlyArray<DropdownNavItem> = [
  { label: 'Pricing', href: '/pricing' },
  {
    label: 'Solutions',
    menu: {
      feature: {
        title: 'Lifecycle automations',
        description: "Flows that run on your product's events",
        href: '/browse/flows',
        visual: {
          kind: 'stack',
          images: [
            {
              src: '/landing/home/lifecycle/welcome.webp',
              width: 520,
              height: 800,
            },
            {
              src: '/landing/home/lifecycle/account-ready.webp',
              width: 520,
              height: 800,
            },
            {
              src: '/landing/home/lifecycle/first-campaign.webp',
              width: 520,
              height: 800,
            },
          ],
          backdrop: '/landing/home/lifecycle/sky.webp',
        },
      },
      groups: [
        {
          items: [
            {
              label: 'Marketers',
              description: 'On-brand campaigns from one prompt',
              href: '/marketers',
              icon: Megaphone,
            },
            {
              label: 'Startups',
              description: 'Product email from the API and SDK',
              href: '/startups',
              icon: Rocket,
            },
            {
              label: 'Agencies',
              description: "Every client's brand in one workspace",
              href: '/agencies',
              icon: Briefcase,
            },
            {
              label: 'Ecommerce',
              description: 'Campaigns and flows from your Shopify store',
              href: '/ecommerce',
              icon: ShoppingBag,
            },
          ],
        },
      ],
    },
  },
  {
    label: 'Resources',
    menu: {
      feature: {
        title: 'Templates',
        description: 'Emails your brand would actually send',
        href: '/browse/templates',
        visual: {
          kind: 'fan',
          backdrop: '/landing/home/templates/sky.webp',
          images: [
            {
              src: '/landing/home/templates/opal.webp',
              width: 520,
              height: 800,
            },
            {
              src: '/landing/home/templates/tiffany.webp',
              width: 520,
              height: 800,
            },
            {
              src: '/landing/home/templates/sonos.webp',
              width: 520,
              height: 800,
            },
          ],
        },
      },
      groups: [
        {
          items: [
            {
              label: 'Integrations',
              description: 'Stripe, Shopify, Clerk, Supabase, and more',
              href: '/browse/integrations',
              icon: Blocks,
            },
            {
              label: 'Blog',
              description: 'Playbooks and product news',
              href: '/blog',
              icon: Newspaper,
            },
            {
              label: 'Customers',
              description: 'What teams moved from, and what they report',
              href: '/customers',
              icon: Users,
            },
            {
              label: 'API',
              description: 'Endpoints, parameters, and responses',
              href: 'https://docs.brew.new/api-reference/api/api-introduction',
              icon: Terminal,
              external: true,
            },
            {
              label: 'Brew University',
              description: 'Video walkthroughs from the founders',
              href: '/university',
              icon: GraduationCap,
            },
            {
              label: 'Support',
              description: 'Get help from the team',
              href: '/support',
              icon: LifeBuoy,
            },
          ],
        },
      ],
    },
  },
  { label: 'Docs', href: 'https://docs.brew.new/', external: true },
]

/** The bar's right-hand text link, beside the sign-up CTA. */
export const NAV_MCP_LINK = { label: 'MCP', href: '/mcp' } as const
